import React, { useState, useRef, useEffect } from "react";
import { Button, StyledModal } from "@bikdotai/bik-component-library";
import UploadIcon from "../../../icons/UploadIcon";
import { FirebaseService } from "../../../services/firebase/FirebaseService";
import { voidFunction } from "../../../app/messageBlock/Constants";
import { getDownloadURL } from "firebase/storage";
import { useAppSelector } from "../../../state/store";
import { ServiceScreenFeaturesService } from "../../services/ServiceScreenFeaturesService";
import { ServiceScreenFeature } from "../../models/serviceFeatures/ServiceScreenFeatureModel";
import * as XLSX from "xlsx";

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<{
    successCount?: number;
    errorCount?: number;
    errorFileUrl?: string;
    warnings?: string[];
  } | null>(null);
  const [filePreview, setFilePreview] = useState<{
    totalCount: number;
    duplicates: Array<{ feature_id: string; name: string; existingFeature?: ServiceScreenFeature }>;
    file: File;
  } | null>(null);
  const [existingFeatures, setExistingFeatures] = useState<ServiceScreenFeature[]>([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const homeState = useAppSelector((state) => state.homeState);
  const firebaseService = new FirebaseService(voidFunction, voidFunction);
  const service = new ServiceScreenFeaturesService();

  // Fetch existing features on mount to check for duplicates
  useEffect(() => {
    const fetchExistingFeatures = async () => {
      try {
        const response = await service.getFeaturesList();
        setExistingFeatures(response.response.features);
      } catch (error) {
        console.error("Error fetching existing features:", error);
      }
    };
    if (isOpen) {
      fetchExistingFeatures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const getFileName = (fileName: string) => {
    const extension = fileName.split(".").pop();
    const date = new Date();
    return `service-features/bulk-uploads/${date.getTime()}.${extension}`;
  };

  const getMediaPath = (path: string) => {
    return `chatbot/${homeState.storeId || "admin"}/${path}`;
  };

  const parseExcelFile = async (file: File): Promise<Array<{ feature_id: string; name: string }>> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet) as Array<Record<string, unknown>>;
          
          // Extract feature_id and name from each row
          const features = jsonData
            .map((row) => {
              // Try different possible column names (case-insensitive)
              const featureId = 
                row.feature_id || 
                row["feature_id"] || 
                row["Feature ID"] || 
                row["feature ID"] ||
                row["FEATURE_ID"] ||
                "";
              const name = 
                row.name || 
                row["name"] || 
                row["Name"] || 
                row["NAME"] ||
                "";
              
              return {
                feature_id: String(featureId || "").trim(),
                name: String(name || "").trim(),
              };
            })
            .filter((f) => f.feature_id); // Only include rows with feature_id
          
          resolve(features);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  const uploadFileToFirebase = async (file: File): Promise<string> => {
    const fileName = getFileName(file.name);
    const uploadRef = firebaseService.getUploadRef(file, getMediaPath(fileName));

    return new Promise<string>((resolve, reject) => {
      uploadRef.on(
        "state_changed",
        (snapshot) => {
          const percent = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(percent);
        },
        (error) => {
          console.error("Error uploading file:", error);
          reject(error);
        },
        () => {
          getDownloadURL(uploadRef.snapshot.ref)
            .then((url) => {
              resolve(url);
            })
            .catch(reject);
        }
      );
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      const validExtensions = [".xlsx", ".xls", ".csv"];
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        alert("Please select an Excel (.xlsx, .xls) or CSV file");
        return;
      }

      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert("File size must be less than 10MB");
        return;
      }

      // Parse file and show preview
      setIsLoadingPreview(true);
      setUploadStatus(null);

      try {
        // Parse Excel file
        const features = await parseExcelFile(file);
        const totalCount = features.length;

        // Check for duplicates by comparing feature_id with existing features
        const duplicates = features
          .map((feature) => {
            const existingFeature = existingFeatures.find(
              (existing) => existing.feature_id === feature.feature_id
            );
            if (existingFeature) {
              return {
                feature_id: feature.feature_id,
                name: feature.name,
                existingFeature,
              };
            }
            return null;
          })
          .filter((dup): dup is { feature_id: string; name: string; existingFeature: ServiceScreenFeature } => dup !== null);

        // Set preview data
        setFilePreview({
          totalCount,
          duplicates,
          file,
        });
      } catch (error: any) {
        console.error("Error parsing file:", error);
        alert(`Error parsing file: ${error.message || "Please check the file format."}`);
      } finally {
        setIsLoadingPreview(false);
      }
    }
  };

  const handleConfirmUpload = async () => {
    if (!filePreview) return;

    setIsUploading(true);
    setUploadProgress(0);
    setFilePreview(null);

    try {
      // Step 1: Upload to Firebase Storage
      const firebaseUrl = await uploadFileToFirebase(filePreview.file);

      // Step 2: Use Firebase URL directly (API accepts Firebase Storage URLs)
      const result = await service.bulkUploadFeatures(firebaseUrl);

      setUploadStatus({
        successCount: result.response.successCount,
        errorCount: result.response.errorCount,
        errorFileUrl: result.response.errorFileUrl,
        warnings: result.response.warnings,
      });

      if (result.response.errorCount === 0) {
        // All successful
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      console.error("Error in bulk upload:", error);
      alert(`Error uploading file: ${error.message || "Please try again."}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (!isUploading && !isLoadingPreview) {
      setUploadStatus(null);
      setFilePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onClose();
    }
  };

  const handleDownloadErrorFile = () => {
    if (uploadStatus?.errorFileUrl) {
      // If it's a GCS URL, we might need to convert it or handle it differently
      // For now, try to open it directly
      window.open(uploadStatus.errorFileUrl, "_blank");
    }
  };

  if (isLoadingPreview) {
    return (
      <StyledModal
        open={isOpen}
        headingTitle="Bulk Upload Features"
        onClose={handleClose}
        width="600px"
      >
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
            }}
          >
            <UploadIcon width="40" height="40" fill="#731DCF" />
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Parsing file...
          </h3>
          <p
            style={{ margin: "0 0 32px 0", color: "#6b7280", fontSize: "14px" }}
          >
            Please wait while we analyze your file.
          </p>
        </div>
      </StyledModal>
    );
  }

  if (isUploading) {
    return (
      <StyledModal
        open={isOpen}
        headingTitle="Bulk Upload Features"
        onClose={handleClose}
        width="600px"
      >
        <div style={{ padding: "40px 24px", textAlign: "center" }}>
          <div
            style={{
              backgroundColor: "#f5f5f5",
              borderRadius: "50%",
              width: "80px",
              height: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
            }}
          >
            <UploadIcon width="40" height="40" fill="#731DCF" />
          </div>

          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: "#1f2937",
            }}
          >
            Uploading and processing file...
          </h3>
          <p
            style={{ margin: "0 0 32px 0", color: "#6b7280", fontSize: "14px" }}
          >
            Please wait while we upload and process your file.
          </p>

          <div
            style={{
              width: "100%",
              height: "8px",
              backgroundColor: "#e5e7eb",
              borderRadius: "4px",
              marginBottom: "32px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${uploadProgress}%`,
                height: "100%",
                backgroundColor: "#731DCF",
                transition: "width 0.15s ease",
              }}
            />
          </div>
        </div>
      </StyledModal>
    );
  }

  return (
    <StyledModal
      open={isOpen}
      headingTitle="Bulk Upload Features"
      onClose={handleClose}
      width="600px"
    >
      <div style={{ padding: "24px" }}>
        {filePreview ? (
          <div>
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f0f9ff",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1f2937",
                }}
              >
                File Preview
              </h3>
              <div style={{ marginBottom: "8px" }}>
                <span style={{ color: "#1f2937", fontWeight: "500" }}>
                  Total Features: {filePreview.totalCount}
                </span>
              </div>
              {filePreview.duplicates.length > 0 && (
                <div style={{ marginTop: "16px" }}>
                  <p style={{ fontWeight: "500", marginBottom: "8px", color: "#dc2626" }}>
                    ⚠️ {filePreview.duplicates.length} duplicate feature(s) found (These are getting over written): 
                  </p>
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      padding: "8px",
                      backgroundColor: "white",
                    }}
                  >
                    <table style={{ width: "100%", fontSize: "12px" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                          <th style={{ textAlign: "left", padding: "8px", fontWeight: "600" }}>
                            Feature ID
                          </th>
                          <th style={{ textAlign: "left", padding: "8px", fontWeight: "600" }}>
                            Name (New)
                          </th>
                          <th style={{ textAlign: "left", padding: "8px", fontWeight: "600" }}>
                            Existing Name
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filePreview.duplicates.map((dup, index) => (
                          <tr key={index} style={{ borderBottom: "1px solid #f3f4f6" }}>
                            <td style={{ padding: "8px", fontFamily: "monospace" }}>
                              {dup.feature_id}
                            </td>
                            <td style={{ padding: "8px" }}>{dup.name || "-"}</td>
                            <td style={{ padding: "8px" }}>
                              {dup.existingFeature?.name || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ marginTop: "8px", fontSize: "12px", color: "#6b7280" }}>
                    These features will be updated if you proceed.
                  </p>
                </div>
              )}
              {filePreview.duplicates.length === 0 && (
                <div style={{ marginTop: "8px" }}>
                  <span style={{ color: "#059669", fontSize: "14px" }}>
                    ✓ No duplicates found
                  </span>
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button
                buttonText="Cancel"
                onClick={() => {
                  setFilePreview(null);
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
                size="medium"
                style={{
                  color: "#374151",
                  border: "1px solid #d1d5db",
                }}
              />
              <Button
                buttonText="Confirm & Upload"
                onClick={handleConfirmUpload}
                size="medium"
                style={{
                  backgroundColor: "#731DCF",
                  color: "white",
                }}
              />
            </div>
          </div>
        ) : uploadStatus ? (
          <div>
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f0f9ff",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1f2937",
                }}
              >
                Upload Complete
              </h3>
              <div style={{ marginBottom: "8px" }}>
                <span style={{ color: "#059669", fontWeight: "500" }}>
                  ✓ {uploadStatus.successCount} features uploaded successfully
                </span>
              </div>
              {(uploadStatus.errorCount ?? 0) > 0 && (
                <div style={{ marginBottom: "8px" }}>
                  <span style={{ color: "#dc2626", fontWeight: "500" }}>
                    ✗ {uploadStatus.errorCount} features failed
                  </span>
                </div>
              )}
              {uploadStatus.warnings && uploadStatus.warnings.length > 0 && (
                <div style={{ marginTop: "12px" }}>
                  <p style={{ fontWeight: "500", marginBottom: "4px" }}>
                    Warnings:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {uploadStatus.warnings.map((warning, index) => (
                      <li key={index} style={{ color: "#f59e0b", fontSize: "14px" }}>
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadStatus.errorFileUrl && (
                <div style={{ marginTop: "12px" }}>
                  <Button
                    buttonText="Download Error Report"
                    onClick={handleDownloadErrorFile}
                    size="medium"
                    style={{
                      backgroundColor: "#dc2626",
                      color: "white",
                      border: "none",
                    }}
                  />
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button
                buttonText="Close"
                onClick={handleClose}
                size="medium"
              />
            </div>
          </div>
        ) : (
          <div>
            <div
              style={{
                border: "2px dashed #d1d5db",
                borderRadius: "12px",
                padding: "32px 24px",
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "50%",
                  width: "80px",
                  height: "80px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px auto",
                }}
              >
                <UploadIcon width="40" height="40" fill="#731DCF" />
              </div>

              <h3
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1f2937",
                }}
              >
                Upload Excel/CSV File
              </h3>
              <p
                style={{
                  margin: "0 0 24px 0",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                Excel (.xlsx, .xls) or CSV format • Max 10MB
              </p>

              <div
                onClick={handleFileUpload}
                style={{
                  border: "1px solid #731DCF",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "border-color 0.2s ease",
                  width: "100%",
                }}
              >
                <UploadIcon width="20" height="20" fill="#731DCF" />
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#731DCF",
                  }}
                >
                  Choose File
                </span>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <Button
                buttonText="Cancel"
                onClick={handleClose}
                size="medium"
                style={{
                  color: "#731DCF",
                  border: "1px solid #d1d5db",
                }}
              />
            </div>
          </div>
        )}
      </div>
    </StyledModal>
  );
};

export default BulkUploadModal;

